import React, { useState, useEffect } from 'react';
import { auth } from '../../../../../firebase/config';
import Modal from '../../../../../components/Modal';
import TextField from '../../../../../components/TextField';
import Button from '../../../../../components/Button';
import { FiMail, FiPhone, FiHash, FiMapPin, FiX, FiCheckCircle, FiSearch } from 'react-icons/fi';
import Icon from '../../../../../components/Icon';
import { submitCompany, fetchGlobalCompanies, type CompanyFormData } from '../service/addCompanyService';
import type { Company } from '../../../../companies/service/companiesService';
import { fetchCompanyBankId } from '../../../../companies/service/companiesService';
import Toast from '../../../../../components/Toast';

type AddCompanyModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    bankId?: string;
    existingCompanies?: Company[];
};

function AddCompanyModal({ isOpen, onClose, onSuccess, bankId, existingCompanies = [] }: AddCompanyModalProps) {
    const [companyTypes] = useState(['Guard Company', 'BAMS', 'CCTV']);

    // Form states
    const [formData, setFormData] = useState<CompanyFormData>({
        companyName: '',
        companyType: '',
        companyId: '',
        companyNo: '',
        email: '',
        companyAddress: '',
        contactPersonName: '',
        contactMobileNumber: '',
        contactEmail: '',
    });

    // Guard Company Selection
    const [showCompanyModal, setShowCompanyModal] = useState(false);
    const [availableCompanies, setAvailableCompanies] = useState<Company[]>([]);
    const [selectedCompanyFromList, setSelectedCompanyFromList] = useState<Company | null>(null);
    const [companySearch, setCompanySearch] = useState('');

    // CCTV Company Selection
    const [showCCTVModal, setShowCCTVModal] = useState(false);
    const [availableCCTVCompanies, setAvailableCCTVCompanies] = useState<Company[]>([]);
    const [selectedCCTVCompanyFromList, setSelectedCCTVCompanyFromList] = useState<Company | null>(null);
    const [cctvSearch, setCctvSearch] = useState('');

    // BAMS Company Selection
    const [showBAMSModal, setShowBAMSModal] = useState(false);
    const [availableBAMSCompanies, setAvailableBAMSCompanies] = useState<Company[]>([]);
    const [selectedBAMSCompanyFromList, setSelectedBAMSCompanyFromList] = useState<Company | null>(null);
    const [bamsSearch, setBamsSearch] = useState('');

    // UI states
    const [showToast, setShowToast] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const uid = auth.currentUser?.uid;
    const [currentBankId, setCurrentBankId] = useState<string | null>(bankId || null);
    const userRole = sessionStorage.getItem('role');
    const isBankUser = userRole === 'bank';

    // Fetch bankId if not provided
    useEffect(() => {
        if (isOpen && !currentBankId && uid) {
            const fetchBankId = async () => {
                try {
                    const fetchedBankId = await fetchCompanyBankId(uid, isBankUser);
                    if (fetchedBankId) {
                        setCurrentBankId(fetchedBankId);
                    }
                } catch (err) {
                    console.error('Error fetching bankId:', err);
                }
            };
            fetchBankId();
        } else if (bankId) {
            setCurrentBankId(bankId);
        }
    }, [isOpen, bankId, uid, isBankUser, currentBankId]);

    // Fetch available companies
    useEffect(() => {
        if (isOpen) {
            const fetchCompanies = async () => {
                try {
                    const guardCompanies = await fetchGlobalCompanies('Guard Company');
                    setAvailableCompanies(guardCompanies);
                    const cctvCompanies = await fetchGlobalCompanies('CCTV');
                    setAvailableCCTVCompanies(cctvCompanies);
                    const bamsCompanies = await fetchGlobalCompanies('BAMS');
                    setAvailableBAMSCompanies(bamsCompanies);
                } catch (err) {
                    console.error('Error fetching companies:', err);
                }
            };
            fetchCompanies();
        }
    }, [isOpen]);

    // Handle company type change
    const handleCompanyTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedType = e.target.value;
        setFormData(prev => ({ ...prev, companyType: selectedType }));

        // Reset selected company when type changes
        setSelectedCompanyFromList(null);
        setSelectedCCTVCompanyFromList(null);
        setSelectedBAMSCompanyFromList(null);
        setFormData(prev => ({
            ...prev,
            companyName: '',
            companyId: '',
            companyNo: '',
            email: '',
            companyAddress: '',
        }));

        // Show company selection modal based on type
        if (selectedType === 'Guard Company') {
            setShowCompanyModal(true);
        } else if (selectedType === 'CCTV') {
            setShowCCTVModal(true);
        } else if (selectedType === 'BAMS') {
            setShowBAMSModal(true);
        }
    };

    // Select company from list
    const handleSelectCompanyFromList = async (company: Company) => {
        setSelectedCompanyFromList(company);
        setFormData(prev => ({
            ...prev,
            companyName: company.companyName || '',
            companyId: company.companyId || company.id || '',
            companyNo: company.companyNo || '',
            email: company.email || '',
            companyAddress: company.companyAddress || '',
            contactPersonName: company.contactPersonName || '',
            contactMobileNumber: company.contactMobileNumber || '',
            contactEmail: company.contactEmail || '',
        }));
        setShowCompanyModal(false);
    };

    // Select CCTV company from list
    const handleSelectCCTVCompanyFromList = async (company: Company) => {
        setSelectedCCTVCompanyFromList(company);
        setFormData(prev => ({
            ...prev,
            companyName: company.companyName || '',
            companyId: company.companyId || company.id || '',
            companyNo: company.companyNo || '',
            email: company.email || '',
            companyAddress: company.companyAddress || '',
            contactPersonName: company.contactPersonName || '',
            contactMobileNumber: company.contactMobileNumber || '',
            contactEmail: company.contactEmail || '',
        }));
        setShowCCTVModal(false);
    };

    // Select BAMS company from list
    const handleSelectBAMSCompanyFromList = async (company: Company) => {
        setSelectedBAMSCompanyFromList(company);
        setFormData(prev => ({
            ...prev,
            companyName: company.companyName || '',
            companyId: company.companyId || company.id || '',
            companyNo: company.companyNo || '',
            email: company.email || '',
            companyAddress: company.companyAddress || '',
            contactPersonName: company.contactPersonName || '',
            contactMobileNumber: company.contactMobileNumber || '',
            contactEmail: company.contactEmail || '',
        }));
        setShowBAMSModal(false);
    };

    const handleInputChange = (field: keyof CompanyFormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.companyName.trim() || !formData.companyType) {
            newErrors.submit = 'Please fill in company name and type.';
            setShowToast(true);
            setSuccessMessage('❌ Please fill in company name and type.');
            return false;
        }

        // Additional validation for Guard Company, CCTV, and BAMS
        if (formData.companyType === 'Guard Company' || formData.companyType === 'CCTV' || formData.companyType === 'BAMS') {
            if (
                !formData.companyId?.trim() ||
                !formData.companyNo?.trim() ||
                !formData.email?.trim() ||
                !formData.companyAddress?.trim() ||
                !formData.contactPersonName?.trim() ||
                !formData.contactMobileNumber?.trim() ||
                !formData.contactEmail?.trim()
            ) {
                newErrors.submit =
                    'Please fill in all required fields: Company ID, Phone Number, Email, Address, Contact Person Name, Contact Mobile Number, and Contact Email.';
                setShowToast(true);
                setSuccessMessage(newErrors.submit);
                return false;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        if (!uid) {
            setShowToast(true);
            setSuccessMessage('❌ Error: User not authenticated.');
            return;
        }

        // Fetch bankId if not available
        let finalBankId = currentBankId;
        if (!finalBankId) {
            try {
                finalBankId = await fetchCompanyBankId(uid, isBankUser);
                if (finalBankId) {
                    setCurrentBankId(finalBankId);
                }
            } catch (err) {
                console.error('Error fetching bankId:', err);
            }
        }

        if (!finalBankId) {
            setShowToast(true);
            setSuccessMessage('❌ Error: Unable to find bank information. Please try again.');
            return;
        }

        setLoading(true);
        try {
            await submitCompany(
                finalBankId,
                {
                    ...formData,
                    selectedCompanyFromList,
                    selectedCCTVCompanyFromList,
                    selectedBAMSCompanyFromList,
                },
                uid
            );

            setSuccessMessage('✅ Company added successfully!');
            setShowToast(true);

            // Reset form
            setFormData({
                companyName: '',
                companyType: '',
                companyId: '',
                companyNo: '',
                email: '',
                companyAddress: '',
                contactPersonName: '',
                contactMobileNumber: '',
                contactEmail: '',
            });
            setSelectedCompanyFromList(null);
            setSelectedCCTVCompanyFromList(null);
            setSelectedBAMSCompanyFromList(null);
            setCompanySearch('');
            setCctvSearch('');
            setBamsSearch('');
            setErrors({});

            setTimeout(() => {
                onSuccess();
            }, 500);
        } catch (err: any) {
            console.error(err);
            setShowToast(true);
            setSuccessMessage('❌ Failed to add company.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            companyName: '',
            companyType: '',
            companyId: '',
            companyNo: '',
            email: '',
            companyAddress: '',
            contactPersonName: '',
            contactMobileNumber: '',
            contactEmail: '',
        });
        setSelectedCompanyFromList(null);
        setSelectedCCTVCompanyFromList(null);
        setSelectedBAMSCompanyFromList(null);
        setCompanySearch('');
        setCctvSearch('');
        setBamsSearch('');
        setErrors({});
        setShowToast(false);
        onClose();
    };

    // Get set of existing company IDs to filter them out
    const existingCompanyIds = React.useMemo(() => {
        const ids = new Set<string>();
        existingCompanies.forEach(company => {
            if (company.companyId) {
                ids.add(company.companyId);
            }
            // Also check by document id from global collection
            if (company.id) {
                ids.add(company.id);
            }
        });
        return ids;
    }, [existingCompanies]);

    const filteredGuardCompanies = availableCompanies.filter(
        company => {
            // Filter out companies already in the table
            const isAlreadyAdded = company.companyId && existingCompanyIds.has(company.companyId);
            const isAlreadyAddedById = company.id && existingCompanyIds.has(company.id);
            if (isAlreadyAdded || isAlreadyAddedById) {
                return false;
            }
            // Apply search filter
            return (
                company.companyName?.toLowerCase().includes(companySearch.toLowerCase()) ||
                company.companyId?.toLowerCase().includes(companySearch.toLowerCase()) ||
                company.companyNo?.includes(companySearch)
            );
        }
    );

    const filteredCCTVCompanies = availableCCTVCompanies.filter(
        company => {
            // Filter out companies already in the table
            const isAlreadyAdded = company.companyId && existingCompanyIds.has(company.companyId);
            const isAlreadyAddedById = company.id && existingCompanyIds.has(company.id);
            if (isAlreadyAdded || isAlreadyAddedById) {
                return false;
            }
            // Apply search filter
            return (
                company.companyName?.toLowerCase().includes(cctvSearch.toLowerCase()) ||
                company.companyId?.toLowerCase().includes(cctvSearch.toLowerCase()) ||
                company.companyNo?.includes(cctvSearch)
            );
        }
    );

    const filteredBAMSCompanies = availableBAMSCompanies.filter(
        company => {
            // Filter out companies already in the table
            const isAlreadyAdded = company.companyId && existingCompanyIds.has(company.companyId);
            const isAlreadyAddedById = company.id && existingCompanyIds.has(company.id);
            if (isAlreadyAdded || isAlreadyAddedById) {
                return false;
            }
            // Apply search filter
            return (
                company.companyName?.toLowerCase().includes(bamsSearch.toLowerCase()) ||
                company.companyId?.toLowerCase().includes(bamsSearch.toLowerCase()) ||
                company.companyNo?.includes(bamsSearch)
            );
        }
    );

    // Check if a company is selected from any of the selection modals
    const isCompanySelected = selectedCompanyFromList !== null || selectedCCTVCompanyFromList !== null || selectedBAMSCompanyFromList !== null;

    return (
        <>
            <Modal isOpen={isOpen} onClose={handleClose} title="Add Vendor" size="lg">
                <form onSubmit={handleSubmit} className="p-6">
                    <div className="mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Add Vendor</h3>
                    </div>

                    <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <TextField
                                label="Company Name *"
                                value={formData.companyName}
                                onChange={e => handleInputChange('companyName', e.target.value)}
                                placeholder="Enter company name"
                                required
                                readOnly={true}
                            />
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Company Type *
                                </label>
                                <select
                                    value={formData.companyType}
                                    onChange={handleCompanyTypeChange}
                                    required
                                    disabled={isCompanySelected}
                                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-4 pr-10 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                >
                                    <option value="">Select type</option>
                                    {companyTypes.map(type => (
                                        <option key={type} value={type}>
                                            {type}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Selected Company Info */}
                        {(selectedCompanyFromList || selectedCCTVCompanyFromList || selectedBAMSCompanyFromList) && (
                            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start gap-2">
                                        <Icon icon={FiCheckCircle} className="mt-0.5 h-5 w-5 text-blue-600 dark:text-blue-400" />
                                        <div>
                                            <strong className="text-blue-900 dark:text-blue-100">
                                                Selected Company: {(selectedCompanyFromList || selectedCCTVCompanyFromList || selectedBAMSCompanyFromList)?.companyName}
                                            </strong>
                                            <div className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                                                ID: {(selectedCompanyFromList || selectedCCTVCompanyFromList || selectedBAMSCompanyFromList)?.companyId} | Phone:{' '}
                                                {(selectedCompanyFromList || selectedCCTVCompanyFromList || selectedBAMSCompanyFromList)?.companyNo} | Email:{' '}
                                                {(selectedCompanyFromList || selectedCCTVCompanyFromList || selectedBAMSCompanyFromList)?.email}
                                            </div>
                                        </div>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={() => {
                                            setSelectedCompanyFromList(null);
                                            setSelectedCCTVCompanyFromList(null);
                                            setSelectedBAMSCompanyFromList(null);
                                            setFormData(prev => ({
                                                ...prev,
                                                companyType: '',
                                                companyName: '',
                                                companyId: '',
                                                companyNo: '',
                                                email: '',
                                                companyAddress: '',
                                                contactPersonName: '',
                                                contactMobileNumber: '',
                                                contactEmail: '',
                                            }));
                                        }}
                                        className="text-xs"
                                    >
                                        Change
                                    </Button>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <TextField
                                label="Company ID *"
                                value={formData.companyId || ''}
                                onChange={e => handleInputChange('companyId', e.target.value)}
                                placeholder="Enter company ID (e.g., MG-001)"
                                readOnly={true}
                                required={formData.companyType === 'Guard Company' || formData.companyType === 'CCTV' || formData.companyType === 'BAMS'}
                            />
                            <TextField
                                label="Phone Number *"
                                type="tel"
                                value={formData.companyNo || ''}
                                onChange={e => handleInputChange('companyNo', e.target.value)}
                                placeholder="Enter phone number (e.g., +92-51-274-6011)"
                                readOnly={true}
                                required={formData.companyType === 'Guard Company' || formData.companyType === 'CCTV' || formData.companyType === 'BAMS'}
                            />
                        </div>

                        <TextField
                            label="Email *"
                            type="email"
                            value={formData.email || ''}
                            onChange={e => handleInputChange('email', e.target.value)}
                            placeholder="Enter email address (e.g., info@metroguardspk.com)"
                            readOnly={true}
                            required={formData.companyType === 'Guard Company' || formData.companyType === 'CCTV' || formData.companyType === 'BAMS'}
                        />

                        <div>
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Company Address *
                            </label>
                            <textarea
                                value={formData.companyAddress || ''}
                                onChange={e => handleInputChange('companyAddress', e.target.value)}
                                placeholder="Enter company address"
                                rows={3}
                                readOnly={true}
                                required={formData.companyType === 'Guard Company' || formData.companyType === 'CCTV' || formData.companyType === 'BAMS'}
                                className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm outline-none transition placeholder:text-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                            />
                        </div>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <TextField
                                label="Contact Person Name *"
                                value={formData.contactPersonName || ''}
                                onChange={e => handleInputChange('contactPersonName', e.target.value)}
                                placeholder="Enter contact person name"
                                readOnly={true}
                                required={formData.companyType === 'Guard Company' || formData.companyType === 'CCTV' || formData.companyType === 'BAMS'}
                            />
                            <TextField
                                label="Contact Mobile Number *"
                                type="tel"
                                value={formData.contactMobileNumber || ''}
                                onChange={e => handleInputChange('contactMobileNumber', e.target.value)}
                                placeholder="Enter contact mobile number (e.g., +92-300-1234567)"
                                readOnly={true}
                                required={formData.companyType === 'Guard Company' || formData.companyType === 'CCTV' || formData.companyType === 'BAMS'}
                            />
                        </div>

                        <TextField
                            label="Contact Email *"
                            type="email"
                            value={formData.contactEmail || ''}
                            onChange={e => handleInputChange('contactEmail', e.target.value)}
                            placeholder="Enter contact email address"
                            readOnly={true}
                            required={formData.companyType === 'Guard Company' || formData.companyType === 'CCTV' || formData.companyType === 'BAMS'}
                        />
                    </div>

                    <div className="mt-6 flex gap-3">
                        <Button type="button" variant="secondary" onClick={handleClose} className="flex-1" disabled={loading}>
                            Cancel
                        </Button>
                        <Button type="submit" className="flex-1" disabled={loading || !isCompanySelected}>
                            {loading ? 'Adding Vendor...' : 'Add Vendor'}
                        </Button>
                    </div>
                </form>
            </Modal>

            {/* Guard Company Selection Modal */}
            <Modal
                isOpen={showCompanyModal}
                onClose={() => {
                    setShowCompanyModal(false);
                    setFormData(prev => ({ ...prev, companyType: '' }));
                }}
                title="Select Guard Company"
                size="xl"
            >
                <div className="p-6 space-y-4">
                    <div className="relative">
                        <Icon icon={FiSearch} className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search companies..."
                            value={companySearch}
                            onChange={e => setCompanySearch(e.target.value)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                        />
                    </div>

                    <div className="max-h-96 overflow-y-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Company Name</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Company ID</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Phone</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Email</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredGuardCompanies.map(company => (
                                    <tr
                                        key={company.id}
                                        className="border-b border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/50"
                                    >
                                        <td className="px-4 py-3 text-sm">
                                            <strong>{company.companyName}</strong>
                                        </td>
                                        <td className="px-4 py-3 text-sm">{company.companyId}</td>
                                        <td className="px-4 py-3 text-sm">{company.companyNo}</td>
                                        <td className="px-4 py-3 text-sm">{company.email}</td>
                                        <td className="px-4 py-3">
                                            <Button onClick={() => handleSelectCompanyFromList(company)} className="text-xs px-3 py-1.5">
                                                Select
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {filteredGuardCompanies.length === 0 && (
                            <div className="py-12 text-center">
                                <p className="text-gray-500 dark:text-gray-400">No guard companies found.</p>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-3">
                        <Button variant="secondary" onClick={() => {
                            setShowCompanyModal(false);
                            setFormData(prev => ({ ...prev, companyType: '' }));
                        }}>
                            Cancel
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* CCTV Company Selection Modal */}
            <Modal
                isOpen={showCCTVModal}
                onClose={() => {
                    setShowCCTVModal(false);
                    setFormData(prev => ({ ...prev, companyType: '' }));
                }}
                title="Select CCTV Company"
                size="xl"
            >
                <div className="p-6 space-y-4">
                    <div className="relative">
                        <Icon icon={FiSearch} className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search CCTV companies..."
                            value={cctvSearch}
                            onChange={e => setCctvSearch(e.target.value)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                        />
                    </div>

                    <div className="max-h-96 overflow-y-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Company Name</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Company ID</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Phone</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Email</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCCTVCompanies.map(company => (
                                    <tr
                                        key={company.id}
                                        className="border-b border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/50"
                                    >
                                        <td className="px-4 py-3 text-sm">
                                            <strong>{company.companyName}</strong>
                                        </td>
                                        <td className="px-4 py-3 text-sm">{company.companyId}</td>
                                        <td className="px-4 py-3 text-sm">{company.companyNo}</td>
                                        <td className="px-4 py-3 text-sm">{company.email}</td>
                                        <td className="px-4 py-3">
                                            <Button onClick={() => handleSelectCCTVCompanyFromList(company)} className="text-xs px-3 py-1.5">
                                                Select
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {filteredCCTVCompanies.length === 0 && (
                            <div className="py-12 text-center">
                                <p className="text-gray-500 dark:text-gray-400">No CCTV companies found.</p>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-3">
                        <Button variant="secondary" onClick={() => {
                            setShowCCTVModal(false);
                            setFormData(prev => ({ ...prev, companyType: '' }));
                        }}>
                            Cancel
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* BAMS Company Selection Modal */}
            <Modal
                isOpen={showBAMSModal}
                onClose={() => {
                    setShowBAMSModal(false);
                    setFormData(prev => ({ ...prev, companyType: '' }));
                }}
                title="Select BAMS Company"
                size="xl"
            >
                <div className="p-6 space-y-4">
                    <div className="relative">
                        <Icon icon={FiSearch} className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search BAMS companies..."
                            value={bamsSearch}
                            onChange={e => setBamsSearch(e.target.value)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                        />
                    </div>

                    <div className="max-h-96 overflow-y-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Company Name</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Company ID</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Phone</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Email</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredBAMSCompanies.map(company => (
                                    <tr
                                        key={company.id}
                                        className="border-b border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/50"
                                    >
                                        <td className="px-4 py-3 text-sm">
                                            <strong>{company.companyName}</strong>
                                        </td>
                                        <td className="px-4 py-3 text-sm">{company.companyId}</td>
                                        <td className="px-4 py-3 text-sm">{company.companyNo}</td>
                                        <td className="px-4 py-3 text-sm">{company.email}</td>
                                        <td className="px-4 py-3">
                                            <Button onClick={() => handleSelectBAMSCompanyFromList(company)} className="text-xs px-3 py-1.5">
                                                Select
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {filteredBAMSCompanies.length === 0 && (
                            <div className="py-12 text-center">
                                <p className="text-gray-500 dark:text-gray-400">No BAMS companies found.</p>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-3">
                        <Button variant="secondary" onClick={() => {
                            setShowBAMSModal(false);
                            setFormData(prev => ({ ...prev, companyType: '' }));
                        }}>
                            Cancel
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Toast */}
            {showToast && (
                <div className="pointer-events-none fixed right-4 top-4 z-50">
                    <div className="pointer-events-auto">
                        <Toast
                            message={successMessage}
                            type={successMessage.includes('❌') ? 'error' : 'success'}
                            onClose={() => setShowToast(false)}
                        />
                    </div>
                </div>
            )}
        </>
    );
}

export default AddCompanyModal;
